import type {CopilotChatModel} from './copilot-chat-types'
import {isCustomModel} from './models'

/**
 * Model tier configuration
 */
export interface ModelTierConfig {
  lowTier: CopilotChatModel[]
  midTier: CopilotChatModel[]
  highTier: CopilotChatModel[]
  base: CopilotChatModel[]
  preview: CopilotChatModel[]
  custom: {[groupName: string]: CopilotChatModel[]}
}

/**
 * Low tier model exceptions - models that should be in low tier despite billing multiplier
 */
const LOW_TIER_MODEL_EXCEPTIONS = ['o3-mini', 'Gemini 2.0 Flash']

/**
 * Determines if a model belongs to the low tier based on category or billing
 */
function isLowTierModel(model: CopilotChatModel): boolean {
  // If category field is present, use it
  if (model.model_picker_category !== undefined) {
    return model.model_picker_category === 'lightweight'
  }

  // Fallback to billing multiplier logic
  return (
    (model.billing &&
      ((model.billing?.multiplier < 1 && model.billing?.multiplier !== 0) ||
        LOW_TIER_MODEL_EXCEPTIONS.includes(model.displayName))) ||
    false
  )
}

/**
 * Determines if a model belongs to the mid tier based on category or billing
 */
function isMidTierModel(model: CopilotChatModel): boolean {
  // If category field is present, use it
  if (model.model_picker_category !== undefined) {
    return model.model_picker_category === 'versatile'
  }

  // Fallback to billing multiplier logic
  return (
    (model.billing &&
      (model.billing?.multiplier === 1 || model.billing?.multiplier === 0) &&
      !LOW_TIER_MODEL_EXCEPTIONS.includes(model.displayName)) ||
    false
  )
}

/**
 * Determines if a model belongs to the high tier based on category or billing
 */
function isHighTierModel(model: CopilotChatModel): boolean {
  // If category field is present, use it
  if (model.model_picker_category !== undefined) {
    return model.model_picker_category === 'powerful'
  }

  // Fallback to billing multiplier logic
  return (model.billing && model.billing?.multiplier > 1) || false
}

/**
 * Checks if models have diversity in their categories (either by category field or billing)
 */
export function hasModelCategoryDiversity(models: CopilotChatModel[]): boolean {
  // Check for category diversity or billing diversity as fallback
  const categoryDiversity =
    models.some(m => m.model_picker_category !== undefined) &&
    new Set(models.filter(m => m.model_picker_category !== undefined).map(m => m.model_picker_category)).size > 1

  const billingDiversity =
    models.some(m => m.billing?.multiplier !== undefined) &&
    new Set(models.filter(m => m.billing?.multiplier !== undefined).map(m => m.billing?.multiplier)).size > 1

  return categoryDiversity || billingDiversity
}

/**
 * Categorizes models into tiers for the model picker
 */
export function categorizeModels(models: CopilotChatModel[], shouldShowCategories: boolean): ModelTierConfig {
  const [customModels, defaultModels] = models.reduce<[CopilotChatModel[], CopilotChatModel[]]>(
    (acc, m) => {
      acc[isCustomModel(m) ? 0 : 1].push(m)
      return acc
    },
    [[], []],
  )
  const customModelsByGroupName = customModels.reduce(
    (acc, m) => {
      const groupName = m.custom_model!.owner_name
      acc[groupName] ||= []
      acc[groupName].push(m)
      return acc
    },
    {} as {[groupName: string]: CopilotChatModel[]},
  )
  if (!shouldShowCategories) {
    return {
      lowTier: [],
      midTier: [],
      highTier: [],
      base: defaultModels.filter(m => !m.preview),
      preview: defaultModels.filter(m => m.preview),
      custom: customModelsByGroupName,
    }
  }
  return {
    lowTier: defaultModels.filter(m => isLowTierModel(m)),
    midTier: defaultModels.filter(m => isMidTierModel(m)),
    highTier: defaultModels.filter(m => isHighTierModel(m)),
    base: [],
    preview: [],
    custom: customModelsByGroupName,
  }
}
